<template>
  <v-dialog :model-value="modelValue" max-width="1000" scrollable persistent @update:model-value="requestClose">
    <v-card>
      <v-card-title class="d-flex align-center flex-wrap" style="gap: 8px;">
        <v-icon class="mr-1">mdi-file-document-edit-outline</v-icon>
        <span>{{ t('admConfig.skillEditor.title') }}</span>
        <code class="text-body-2">{{ skillId }}.md</code>
        <v-chip v-if="!loading && !exists" size="small" label color="primary" variant="tonal">
          {{ t('admConfig.skillEditor.isNew') }}
        </v-chip>
      </v-card-title>

      <v-card-text style="max-height: 70vh;">
        <v-alert v-if="error" type="error" variant="tonal" density="compact" class="mb-3">
          {{ error }}
        </v-alert>

        <div v-if="loading" class="d-flex justify-center py-8">
          <v-progress-circular indeterminate color="primary" />
        </div>

        <template v-else>
          <div class="d-flex align-center flex-wrap mb-2" style="gap: 4px;">
            <v-btn size="x-small" variant="text" @click="insertSnippet(FRONTMATTER_SNIPPET)">
              {{ t('admConfig.skillEditor.snippetFrontmatter') }}
            </v-btn>
            <v-btn size="x-small" variant="text" @click="insertSnippet(STEPS_SNIPPET)">
              {{ t('admConfig.skillEditor.snippetSteps') }}
            </v-btn>
            <v-btn size="x-small" variant="text" @click="insertSnippet(OUTPUT_SNIPPET)">
              {{ t('admConfig.skillEditor.snippetOutput') }}
            </v-btn>
          </div>

          <v-textarea
            ref="editorRef"
            v-model="content"
            :label="t('admConfig.skillEditor.source')"
            rows="22"
            density="compact"
            class="skill-source"
            hide-details
          />

          <div class="d-flex align-center flex-wrap mt-2" style="gap: 12px;">
            <span class="text-caption text-medium-emphasis">
              {{ t('admConfig.skillEditor.size', { kb: sizeKb }) }}
            </span>
            <span class="text-caption text-medium-emphasis">
              {{ t('admConfig.skillEditor.takesEffect') }}
            </span>
          </div>
        </template>
      </v-card-text>

      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" :disabled="saving" @click="requestClose(false)">
          {{ t('admConfig.statusEditor.cancel') }}
        </v-btn>
        <v-btn
          variant="flat"
          color="primary"
          :loading="saving"
          :disabled="loading || !content.trim()"
          @click="save"
        >
          {{ t('admConfig.skillEditor.save') }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Chiusura con modifiche non salvate: il testo della skill non è recuperabile altrove -->
    <v-dialog v-model="confirmCloseDialog" max-width="480" persistent>
      <v-card>
        <v-card-title>{{ t('admConfig.skillEditor.confirmCloseTitle') }}</v-card-title>
        <v-card-text>{{ t('admConfig.skillEditor.confirmCloseText') }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="confirmCloseDialog = false">
            {{ t('admConfig.statusEditor.cancel') }}
          </v-btn>
          <v-btn variant="flat" color="error" @click="discardAndClose">
            {{ t('admConfig.skillEditor.discard') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useProjectStore } from '../stores/projects.js'

const { t } = useI18n()
const projectStore = useProjectStore()

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  skillId: { type: String, default: '' }
})

const emit = defineEmits(['update:modelValue', 'saved'])

const content = ref('')
const originalContent = ref('')
const exists = ref(false)
const loading = ref(false)
const saving = ref(false)
const error = ref('')
const confirmCloseDialog = ref(false)
const editorRef = ref(null)

const sizeKb = computed(() => (new Blob([content.value]).size / 1024).toFixed(1))
const dirty = computed(() => content.value !== originalContent.value)

// Scheletro di una skill nuova: frontmatter + i due blocchi che ogni verifica deve avere.
// Il contratto di `Saída` è quello che il Backend si aspetta di ritrovare nell'esito.
const FRONTMATTER_SNIPPET = `---
name: nome-da-skill
description: >
  Uma frase sobre o que esta verificação faz.
---
`

const STEPS_SNIPPET = `
## O que fazer

1.
2.
`

const OUTPUT_SNIPPET = `
## Saída

- \`summary\`: resumo legível do resultado.
- \`canProceed\`: \`true\` se o processo pode avançar, \`false\` se há problemas a corrigir.
- \`verdict\`: \`"ok"\` | \`"warning"\` | \`"error"\`.
- \`findings\`: lista de \`{ severity, field?, message }\`.
`

const skeleton = () => `${FRONTMATTER_SNIPPET}
# ${props.skillId}
${STEPS_SNIPPET}${OUTPUT_SNIPPET}`

// Inserimento alla posizione del cursore, come l'editor delle istruzioni dell'editor stati
const insertSnippet = (snippet) => {
  const textarea = editorRef.value?.$el?.querySelector('textarea')
  const at = textarea ? textarea.selectionStart : content.value.length
  content.value = content.value.slice(0, at) + snippet + content.value.slice(at)
}

const load = async () => {
  loading.value = true
  error.value = ''
  content.value = ''
  originalContent.value = ''
  exists.value = false

  try {
    const result = await projectStore.loadSkill(props.skillId)
    exists.value = !!result?.exists
    content.value = exists.value ? (result.content || '') : skeleton()

    // Un file nuovo parte "sporco": il contenuto proposto non esiste ancora su disco
    originalContent.value = exists.value ? content.value : ''
  } catch (err) {
    error.value = err?.message || t('admConfig.skillEditor.loadError')
  } finally {
    loading.value = false
  }
}

const save = async () => {
  saving.value = true
  error.value = ''
  try {
    await projectStore.saveSkill(props.skillId, content.value)
    originalContent.value = content.value
    exists.value = true
    emit('saved', { skillId: props.skillId })
    emit('update:modelValue', false)
  } catch (err) {
    error.value = err?.message || t('admConfig.skillEditor.saveError')
  } finally {
    saving.value = false
  }
}

const requestClose = (value) => {
  if (value === true) return
  if (dirty.value) {
    confirmCloseDialog.value = true
    return
  }
  emit('update:modelValue', false)
}

const discardAndClose = () => {
  confirmCloseDialog.value = false
  emit('update:modelValue', false)
}

watch(() => props.modelValue, (open) => {
  if (open && props.skillId) load()
})
</script>

<style scoped>
.skill-source :deep(textarea) {
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 0.78rem;
  line-height: 1.45;
}
</style>
